<template>
    <div class="group grid md:grid-cols-[auto_1fr] gap-y-8 gap-x-12">
        <div
            ref="yearContainer"
            class="text-history-year-mobile md:text-history-year-tablet xl:text-history-year-desktop font-semibold md:-mb-60 group-last:md:mb-0">
            <!-- Inline year marker inside the list. Desktop sticky year is rendered separately by the parent. -->
            <div ref="year" class="md:min-w-[4ch] tabular-nums bg-white text-black-800 2xl:ml-[41px]" :class="[inactiveYearClass, hideYearClass]">
                {{ year }}
            </div>
        </div>
        <div class="grid gap-8" :class="{ 'md:grid-cols-2': hasImage }">
            <div class="self-end" :class="{ 'md:mb-6': hasImage && hasImageCaption }">
                <slot name="text" />
            </div>
            <div v-if="hasImage" :class="{ 'md:order-first': imagePosition === 'imageLeft' }">
                <slot name="image" />
            </div>
        </div>
    </div>
</template>

<script lang="ts">
import { PropType } from 'vue';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { screens } from '../../plugins/breakpoints';
import debounce from 'lodash/debounce';

const DESKTOP_YEAR_FADE_START = 'top 250';
const DESKTOP_YEAR_FADE_END = 'top 190';
const DESKTOP_YEAR_FLIP_START = 'top 170';
const DESKTOP_YEAR_FLIP_END = 'bottom top';
const MOBILE_ACTIVE_YEAR_START = 'top 80%';
const MOBILE_ACTIVE_YEAR_END = 'bottom 80%';

export default {
    inject: ['register', 'thePrevYear', 'setActiveYear', 'flipInYear', 'flipOutYear'],
    props: {
        year: { type: String, required: true },
        hasImage: { type: Boolean },
        hasImageCaption: { type: Boolean },
        imagePosition: { type: String as PropType<'imageLeft' | 'imageRight'> },
        editMode: { type: Boolean },
    },
    data() {
        return {
            prevYear: undefined as string | undefined,
            firstOfYear: false,
            // Lifecycle handles for cleaning up responsive triggers and resize observation.
            matchMedia: null as ReturnType<typeof gsap.matchMedia> | null,
            resizeObserver: null as ResizeObserver | null,
        };
    },
    computed: {
        inactiveYearClass() {
            return this.firstOfYear ? 'md:text-black-200' : 'md:invisible';
        },
        hideYearClass() {
            // hide the year on mobile if this is not the first entry of the year
            return this.firstOfYear ? '' : 'hidden md:block';
        },
    },
    mounted() {
        this.registerEntry();

        gsap.registerPlugin(ScrollTrigger);

        this.matchMedia = gsap.matchMedia();
        this.setupDesktopYearAnimation();
        this.setupMobileActiveYear();
        this.setupResizeObserver();
    },
    unmounted() {
        this.matchMedia?.revert();
        this.resizeObserver?.disconnect();
    },
    methods: {
        registerEntry() {
            // Register this entry as a scroll target for the timeline and as part of its year group.
            this.firstOfYear = this.register({
                year: this.year,
                element: this.$el as HTMLElement,
            });
            // in edit mode and for non-first entries we do not set the prevYear, to prevent the animation
            if (this.firstOfYear && !this.editMode) {
                this.prevYear = this.thePrevYear(this.year);
            }
        },
        setupDesktopYearAnimation() {
            // add flip animation on desktop
            this.matchMedia.add(`(min-width: ${screens.md}px)`, () => {
                if (this.firstOfYear) {
                    // Fade the inline year out before the sticky year takes over.
                    gsap.fromTo(
                        this.$refs.year as HTMLElement,
                        { opacity: 1 },
                        {
                            opacity: 0,
                            ease: 'none',
                            scrollTrigger: {
                                trigger: this.$refs.yearContainer as HTMLElement,
                                start: DESKTOP_YEAR_FADE_START,
                                end: DESKTOP_YEAR_FADE_END,
                                scrub: true,
                            },
                        },
                    );
                }
                // The first entry of a year activates that year in the shared sticky label.
                gsap.from(this.$refs.year as HTMLElement, {
                    scrollTrigger: {
                        trigger: this.$refs.yearContainer as HTMLElement,
                        start: DESKTOP_YEAR_FLIP_START,
                        end: DESKTOP_YEAR_FLIP_END,
                        onEnter: this.flipIn,
                        onLeaveBack: this.flipOut,
                    },
                });

                return () => {
                    this.flipOut();
                };
            });
        },
        setupMobileActiveYear() {
            // update active year on mobile
            this.matchMedia.add(`(max-width: ${screens.md}px)`, () => {
                gsap.from(this.$el as HTMLElement, {
                    scrollTrigger: {
                        trigger: this.$el as HTMLElement,
                        start: MOBILE_ACTIVE_YEAR_START,
                        end: MOBILE_ACTIVE_YEAR_END,
                        onEnter: () => {
                            this.setActiveYear(this.year);
                        },
                        onLeaveBack: () => {
                            if (this.firstOfYear && this.prevYear) {
                                this.setActiveYear(this.prevYear);
                            }
                        },
                    },
                });
            });
        },
        setupResizeObserver() {
            // make sure the scroll trigger points are refreshed on resize
            this.resizeObserver = new ResizeObserver(debounce(ScrollTrigger.refresh, 300));
            this.resizeObserver.observe(document.body);
        },
        flipIn(): void {
            if (!this.firstOfYear) {
                return;
            }
            // Activate this year in the shared sticky label.
            this.flipInYear(this.year, this.prevYear);
        },
        flipOut(): void {
            if (!this.firstOfYear) {
                return;
            }
            // Restore the previous year when scrolling back above this entry.
            this.flipOutYear(this.prevYear);
        },
    },
};
</script>
